import { getLink } from "@utils/linkUtils";
import { mailRegex } from "@utils/textUtils";
import { useLocale, useTranslations } from "next-intl";
import React, { useEffect, useState } from "react";
import { useFormState } from "react-dom";

import Input from "@/components/ui/Form/Input/Input";
import Icon from "@/components/ui/Icon/Icon";
import { newsletterTracking, policyTracking } from "@/dataLayer/dataLayer";
import { CustomLink } from "@/types";

import TermsAndPrivacy from "../Checkout/TermsAndPrivacy";
import AnchorLink from "../ui/AnchorLink/AnchorLink";
import "./NewsletterForm.css";
import { SubmitButton } from "./SubmitButton";
import handleSubmit from "./action";

const validate = (value: string) => {
  return value.match(mailRegex);
};

const initialState = {
  newsletter: "",
};
interface Props {
  newsLetterFormHyperLink?: CustomLink;
}
export default function NewsletterForm({ newsLetterFormHyperLink }: Props) {
  const t = useTranslations();
  const locale = useLocale();
  const [newsletterValue, setNewsletterValue] = useState("");
  const [error, setError] = useState(false);
  const [isValidEmail, setIsValidEmail] = useState(false);
  const [formSent, setFormSent] = useState<boolean>(false);
  const [state, formAction] = useFormState(handleSubmit, initialState);
  useEffect(() => {
    if (state.message) {
      setError(true);
      newsletterTracking("error");
    }
    if (state.success) {
      setFormSent(true);
      setTimeout(() => {
        setFormSent(false);
        cleanInput();
      }, 5000);
      newsletterTracking("completed");
    }
  }, [state]);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(false);
    setNewsletterValue(e.target.value);
    if (validate(e.target.value)) {
      setIsValidEmail(true);
    } else {
      setIsValidEmail(false);
    }
  };

  const cleanInput = () => {
    setNewsletterValue("");
    setError(false);
  };

  return (
    <>
      {formSent ? (
        <div className="flex flex-col items-center gap-[16px] md:gap-[10px] md:flex-row py-[33px]">
          <Icon name="check-single" size="32" className="stroke-white"></Icon>
          <p className="font-secondary font-normal text-s w-20\/24 text-center md:w-auto">{t("newsletter.subscription.submit.success")}</p>
        </div>
      ) : (
        <>
          <form action={formAction} className="newsletter-form">
            <legend className="text-center mb-s pl-s font-secondary md:text-left md:rtl:text-right">{t("newsletter.subscription.title")}</legend>
            <div className="bg-black-0 flex items-center">
              <label htmlFor="newsletter" className="sr-only">
                {t("newsletter.subscription.input.placeholder")}
              </label>
              <Input
                value={newsletterValue}
                onChange={onChange}
                type="email"
                id="newsletter"
                name="newsletter"
                placeholder={t("newsletter.subscription.input.placeholder")}
                className="h-fit"
                standardHeight={false}
                hideBorder
                wrapperClassNames="flex-1"
              />
              {!isValidEmail && newsletterValue !== "" && (
                <button onClick={cleanInput} type="reset">
                  <Icon role="button" name="close" size="16" className="stroke-white flex flex-wrap items-center mr-s" />
                </button>
              )}
              {isValidEmail && newsletterValue !== "" && (
                <Icon name="check-single" size="16" className="stroke-white flex flex-wrap items-center mx-s my-xs" />
              )}
              <p aria-live="polite" className="sr-only">
                {state?.message}
              </p>
              <SubmitButton error={error} />
            </div>
          </form>
          {error ? (
            <div className="flex mt-xs">
              <Icon name="alert" size="16" className="stroke-status-error"></Icon>
              <p className="text-s text-center md:text-left md:rtl:text-right font-secondary-light pl-[10px]">
                {t("newsletter.subscription.error.email")}
              </p>
            </div>
          ) : (
            <p className="text-center text-s md:text-left md:rtl:text-right font-secondary-light mt-xs pl-s">
              {/* {t("newsletter.subscription.terms")}
              {newsLetterFormHyperLink && (
                <AnchorLink
                  className="font-bold"
                  href={getLink(newsLetterFormHyperLink, locale).toString()}
                  onClick={() => policyTracking(newsLetterFormHyperLink.label)}
                >
                  {newsLetterFormHyperLink?.label}
                </AnchorLink>
              )} */}
              <TermsAndPrivacy translationKey="newsletter.subscription.terms" genericLeadForm={true} />
            </p>
          )}
        </>
      )}
    </>
  );
}
